import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { allProjects } from '../../mocks/projects';

export default function PortfolioDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const project = allProjects.find((p) => p.slug === slug);
  const currentIndex = allProjects.findIndex((p) => p.slug === slug);
  const prevProject = currentIndex > 0 ? allProjects[currentIndex - 1] : null;
  const nextProject = currentIndex < allProjects.length - 1 ? allProjects[currentIndex + 1] : null;

  const galleryImages = project?.slug === 'tommy-hilfiger-mens-store'
    ? [
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/c986291fee88e8cf19aa292fa4cd03c2.jpeg',
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/a1241d749fc0b378807656d51ab5d82d.jpeg',
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/d7d715b56235a7ebf0d2068dfc53f0d2.jpeg',
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/ff8c56b430acb53e0eeb53dc29edbd1f.jpeg',
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/1a8b3459462ad3afeb96a742dd57e08f.jpeg',
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/3b76ab16b5c7e3e9cdad9923dfd6a68a.jpeg',
      ]
    : project?.slug === 'chanel-womens-shoes'
    ? [
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/33b420c1454cfe321bbe878e32c722c9.jpeg',
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/82ccb37ea3cce70b757781680f35de77.jpeg',
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/9d66a035105711410dd077e4d67ab509.jpeg',
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/398e2fba34b13d739fdf3a3b1d8272ce.jpeg',
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/081ee61de15609f54dbfff68c0f6209f.jpeg',
        'https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/8202f8a1dd7d40b17ee2302d80e97f4a.jpeg',
      ]
    : [
        project?.image ?? '',
        project?.image ?? '',
        project?.image ?? '',
        project?.image ?? '',
        project?.image ?? '',
        project?.image ?? '',
      ];

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);
  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, galleryImages.length]);

  if (!project) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-black/50 mb-4">404</p>
          <h1 className="text-4xl font-bold mb-6">Project Not Found</h1>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-widest border-b-2 border-black pb-1 hover:opacity-60 transition-opacity cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Portfolio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[55vh] w-full overflow-hidden">
        <img
          src={project.image}
          alt={project.title}
          className={`w-full h-full object-cover ${project.slug === 'chanel-womens-shoes' ? 'object-center' : 'object-top'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 sm:px-8 md:px-20 w-full">
          <p className="text-white/60 text-xs uppercase tracking-[0.35em] mb-5 font-light">Project</p>
          <h1 className="text-white text-4xl sm:text-5xl md:text-7xl font-bold leading-tight max-w-4xl">
            {project.title.split(' ').slice(0, -1).join(' ')}<br />
            <span className="italic font-light">{project.title.split(' ').slice(-1)}</span>
          </h1>
          <div className="w-12 h-px bg-white/40 mt-8" />
        </div>
      </section>

      <main>
        {/* Breadcrumb + Meta */}
        <section className="py-10 sm:py-14 px-6 sm:px-8 md:px-20 border-b border-black/10">
          <div className="max-w-7xl mx-auto flex flex-col gap-6">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-widest border-b-2 border-black pb-1 hover:opacity-60 transition-opacity cursor-pointer whitespace-nowrap self-start"
            >
              <i className="ri-arrow-left-line"></i>
              All Projects
            </Link>
            <div className="flex flex-wrap items-center gap-6 sm:gap-12">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-black/40 mb-1">Client</p>
                <p className="text-sm font-semibold">{project.title}</p>
              </div>
              <div className="w-px h-8 bg-black/10 hidden sm:block" />
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-black/40 mb-1">Type</p>
                <p className="text-sm font-semibold">Retail Construction</p>
              </div>
              <div className="w-px h-8 bg-black/10 hidden sm:block" />
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-black/40 mb-1">Location</p>
                <p className="text-sm font-semibold">New York, NY</p>
              </div>
            </div>
          </div>
        </section>

        {/* Project Description */}
        <section className="py-16 sm:py-24 px-6 sm:px-8 md:px-20">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-black/50 mb-5">Project Overview</p>
            <div className="w-12 h-px bg-black mx-auto mb-10" />
            <p className="text-xl sm:text-2xl leading-relaxed text-black/75 font-light mb-12">
              Reiken worked closely with both the store and the vendor to bring this shop-in-shop to life — coordinating every detail from planning through completion.
            </p>
            <Link
              to="/contact-us"
              className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-white hover:text-black border-2 border-black transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Start a Similar Project
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Gallery */}
        <section className="py-16 sm:py-24 px-6 sm:px-8 md:px-20 bg-[#f8f7f5]">
          <div className="max-w-7xl mx-auto">
            <div className="mb-10 sm:mb-14">
              <p className="text-xs uppercase tracking-[0.3em] text-black/50 mb-5">Photography</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                Project<br />
                <span className="italic font-light">Gallery</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {galleryImages.map((image, index) => (
                <div
                  key={index}
                  className={`group relative overflow-hidden cursor-pointer ${index === 0 || index === galleryImages.length - 1 ? 'sm:col-span-2' : ''}`}
                  onClick={() => openLightbox(index)}
                >
                  <div className={`w-full overflow-hidden ${
                    index === 0 || index === galleryImages.length - 1
                      ? 'aspect-[4/3] sm:aspect-[16/7]'
                      : 'aspect-[4/3]'
                  }`}>
                    <img
                      src={image}
                      alt={`${project.title} - Image ${index + 1}`}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-400" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                    <div className="w-12 h-12 flex items-center justify-center border border-white rounded-full">
                      <i className="ri-zoom-in-line text-white text-xl"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Navigation */}
        <section className="border-t border-black/10">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-black/10">
            {prevProject ? (
              <Link
                to={`/portfolio/${prevProject.slug}`}
                className="group flex items-center gap-6 px-8 sm:px-16 py-10 sm:py-14 hover:bg-[#f8f7f5] transition-colors duration-300 cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  <i className="ri-arrow-left-line text-2xl group-hover:-translate-x-1 transition-transform duration-300"></i>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-black/40 mb-2">Previous Project</p>
                  <p className="text-base sm:text-lg font-bold">{prevProject.title}</p>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {nextProject ? (
              <Link
                to={`/portfolio/${nextProject.slug}`}
                className="group flex items-center justify-end gap-6 px-8 sm:px-16 py-10 sm:py-14 hover:bg-[#f8f7f5] transition-colors duration-300 cursor-pointer text-right"
              >
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-black/40 mb-2">Next Project</p>
                  <p className="text-base sm:text-lg font-bold">{nextProject.title}</p>
                </div>
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  <i className="ri-arrow-right-line text-2xl group-hover:translate-x-1 transition-transform duration-300"></i>
                </div>
              </Link>
            ) : (
              <div />
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-24 sm:py-32 px-6 sm:px-8 md:px-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/dd9ac60da2db526df68eccc344d22237.jpg"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/75" />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto text-center text-white">
            <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-6">Ready to Build?</p>
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-6 sm:mb-8">
              Let&apos;s Create Something<br />
              <span className="italic font-light">Extraordinary</span>
            </h2>
            <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-10 sm:mb-12 font-light max-w-xl mx-auto">
              Contact us today to discuss your project. We bring 30 years of expertise to every build.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center gap-3 bg-white text-black px-10 sm:px-12 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-transparent hover:text-white border-2 border-white transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Start a Conversation
              </Link>
              <a
                href="tel:9174320310"
                className="inline-flex items-center justify-center gap-3 border border-white/50 text-white px-8 sm:px-10 py-4 text-sm font-semibold uppercase tracking-widest hover:border-white transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                917-432-0310
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <button
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-white text-2xl sm:text-3xl cursor-pointer hover:opacity-70 transition-opacity border border-white/30 hover:border-white z-10"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <i className="ri-close-line"></i>
          </button>
          <button
            className="absolute left-2 sm:left-8 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-white cursor-pointer hover:opacity-70 transition-opacity border border-white/30 hover:border-white z-10"
            onClick={prevImage}
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-line text-lg sm:text-xl"></i>
          </button>
          <div className="w-full h-full flex items-center justify-center px-14 sm:px-24 py-16 sm:py-20">
            <img
              src={galleryImages[currentImageIndex]}
              alt={`${project.title} - Image ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <button
            className="absolute right-2 sm:right-8 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-white cursor-pointer hover:opacity-70 transition-opacity border border-white/30 hover:border-white z-10"
            onClick={nextImage}
            aria-label="Next image"
          >
            <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
          </button>
          <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-xs uppercase tracking-widest z-10">
            {currentImageIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
